import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuthState } from "react-firebase-hooks/auth";
import { auth, authService } from "../firebase";
import { ChevronLeft, ChevronRight } from "lucide-react";

// Google Icon Component
const GoogleIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 48 48">
    <path fill="#FFC107" d="M43.611 20.083H42V20H24v8h11.303c-1.649 4.657-6.08 8-11.303 8-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 12.955 4 4 12.955 4 24s8.955 20 20 20 20-8.955 20-20c0-2.641-.21-5.236-.611-7.743z" />
    <path fill="#FF3D00" d="M6.306 14.691l6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 16.318 4 9.656 8.337 6.306 14.691z" />
    <path fill="#4CAF50" d="M24 44c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238C29.211 35.091 26.715 36 24 36c-5.202 0-9.619-3.317-11.283-7.946l-6.522 5.025C9.505 39.556 16.227 44 24 44z" />
    <path fill="#1976D2" d="M43.611 20.083H42V20H24v8h11.303c-.792 2.237-2.231 4.166-4.087 5.571l6.19 5.238C42.022 35.026 44 30.038 44 24c0-2.641-.21-5.236-.611-7.743z" />
  </svg>
);

// EduTools Logo Component (dark version for light backgrounds)
const EduToolsLogo = ({ className = "" }) => (
  <svg
    className={className}
    viewBox="0 0 222.68 211.78"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      fill="currentColor"
      d="M222.68,105.89c0,58.48-47.41,105.89-105.89,105.89h-5.45C49.86,211.78,2.45,164.37,2.45,105.89h0C2.45,47.41,49.86,0,108.34,0h5.45c58.48,0,105.89,47.41,105.89,105.89h0Z"
    />
    <path
      fill="white"
      d="M169.37,84.21c-3.71-2.88-9.06-2.21-11.94,1.5l-41.87,53.96-32.16-40.48c-2.85-3.59-8.09-4.19-11.68-1.34-3.59,2.85-4.19,8.09-1.34,11.68l38.26,48.16c1.53,1.93,3.86,3.06,6.33,3.08h.09c2.44,0,4.76-1.09,6.31-2.97l47.5-61.22c2.88-3.71,2.21-9.06-1.5-11.94v-.43Z"
    />
    <circle fill="white" cx="111.34" cy="62.39" r="15.5"/>
  </svg>
);

// Slide images for the carousel - update these paths as needed
const slideImages = [
  {
    src: process.env.PUBLIC_URL + "/images/Bento-Image-01_1-p-1600.png",
    title: "AI-Powered Study Tools",
    description: "Get instant help with any subject"
  },
  {
    src: process.env.PUBLIC_URL + "/images/Bento-Image-07_1-p-1600.png",
    title: "Smart Flashcards",
    description: "Learn faster with personalized practice"
  },
  {
    src: process.env.PUBLIC_URL + "/images/Bento-Image-08_1-p-1600.png",
    title: "Collaborate with Friends",
    description: "Study together, succeed together"
  }
];

function GetStarted() {
  const [user, loading] = useAuthState(auth);
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);

  // Redirect if user is logged in
  useEffect(() => {
    if (user) {
      navigate("/login-success");
    }
  }, [user, navigate]);

  // Auto-advance slides
  useEffect(() => {
    if (!isAutoPlaying) return;
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slideImages.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [isAutoPlaying]);

  const handleGoogleSignIn = async () => {
    try {
      await authService.signInWithGoogle();
      // After successful login, user will be redirected by the useEffect
    } catch (error) {
      console.error("Error signing in with Google:", error);
    }
  };

  const handleEmailContinue = (e) => {
    e.preventDefault();
    if (email) {
      // Navigate to a sign-in page with email pre-filled, or handle email flow
      navigate("/login", { state: { email } });
    }
  };

  const nextSlide = () => {
    setIsAutoPlaying(false);
    setCurrentSlide((prev) => (prev + 1) % slideImages.length);
  };

  const prevSlide = () => {
    setIsAutoPlaying(false);
    setCurrentSlide((prev) => (prev - 1 + slideImages.length) % slideImages.length);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col lg:flex-row bg-white">
      {/* Left Column - Get Started Form */}
      <div className="flex-1 flex items-center justify-center p-8 lg:p-12">
        <div className="w-full max-w-md">
          {/* Logo */}
          <div className="mb-8">
            <Link to="/">
              <EduToolsLogo className="h-12 w-12 text-black" />
            </Link>
          </div>

          {/* Title and Description */}
          <h1 className="text-3xl md:text-4xl font-semibold text-gray-900 mb-3">
            Get started with EduTools
          </h1>
          <p className="text-gray-500 mb-8">
            Your AI-powered study companion. Access smarter learning tools and boost your academic performance.
          </p>

          {/* Google Sign In Button */}
          <button
            onClick={handleGoogleSignIn}
            className="w-full flex items-center justify-center gap-3 px-4 py-3.5 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors mb-4 text-gray-700 font-medium"
          >
            <GoogleIcon />
            Continue with Google
          </button>

          {/* Divider */}
          <div className="relative flex items-center justify-center my-6">
            <div className="w-full border-t border-gray-200"></div>
            <span className="px-4 text-sm text-gray-400 bg-white absolute">OR</span>
          </div>

          {/* Email Input */}
          <form onSubmit={handleEmailContinue}>
            <div className="mb-4">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter an email address"
                className="w-full px-4 py-3.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all text-gray-900 placeholder-gray-400"
              />
              <p className="text-xs text-gray-400 mt-2">
                Use your school or work email to access team features
              </p>
            </div>

            <button
              type="submit"
              className="w-full py-3.5 bg-gray-900 text-white rounded-lg font-medium hover:bg-gray-800 transition-colors"
            >
              Continue with Email
            </button>
          </form>

          {/* Terms */}
          <p className="text-xs text-gray-400 mt-6 text-center">
            By signing up, you agree to our{" "}
            <Link to="/terms-of-service" className="text-gray-600 hover:underline">
              Terms of Service
            </Link>{" "}
            and{" "}
            <Link to="/privacy-policy" className="text-gray-600 hover:underline">
              Privacy Policy
            </Link>
            .
          </p>

          {/* Already have an account - Sign In link */}
          <div className="mt-8 pt-6 border-t border-gray-100">
            <p className="text-center text-gray-500">
              Already have an account?{" "}
              <Link to="/login" className="text-blue-600 font-medium hover:underline">
                Sign In
              </Link>
            </p>
          </div>
        </div>
      </div>

      {/* Right Column - Image Slider */}
      <div className="hidden lg:block flex-1 relative bg-gradient-to-br from-blue-600 via-blue-500 to-cyan-400 m-4 rounded-3xl overflow-hidden">
        {/* Slide Images */}
        <div className="absolute inset-0">
          {slideImages.map((slide, index) => (
            <div
              key={index}
              className={`absolute inset-0 transition-opacity duration-700 ease-in-out ${
                index === currentSlide ? "opacity-100" : "opacity-0"
              }`}
            >
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent z-10" />
              <img
                src={slide.src}
                alt={slide.title}
                className="w-full h-full object-cover"
              />
            </div>
          ))}
        </div>

        {/* Slide Content */}
        <div className="absolute bottom-0 left-0 right-0 p-8 z-20">
          <div className="max-w-md">
            <h2 className="text-2xl md:text-3xl font-semibold text-white mb-2">
              {slideImages[currentSlide].title}
            </h2>
            <p className="text-white/80 text-lg">
              {slideImages[currentSlide].description}
            </p>
          </div>

          {/* Slide Navigation */}
          <div className="flex items-center justify-between mt-8">
            {/* Dots */}
            <div className="flex gap-2">
              {slideImages.map((_, index) => (
                <button
                  key={index}
                  onClick={() => {
                    setIsAutoPlaying(false);
                    setCurrentSlide(index);
                  }}
                  className={`h-2 rounded-full transition-all duration-300 ${
                    index === currentSlide
                      ? "w-8 bg-white"
                      : "w-2 bg-white/40 hover:bg-white/60"
                  }`}
                />
              ))}
            </div>

            {/* Arrow Controls */}
            <div className="flex gap-2">
              <button
                onClick={prevSlide}
                className="p-2 rounded-full bg-white/20 hover:bg-white/30 transition-colors"
              >
                <ChevronLeft className="w-5 h-5 text-white" />
              </button>
              <button
                onClick={nextSlide}
                className="p-2 rounded-full bg-white/20 hover:bg-white/30 transition-colors"
              >
                <ChevronRight className="w-5 h-5 text-white" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default GetStarted;
